import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import type { VoteType } from '../types/alternatives-voting'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useState, useEffect } from 'react'
import { VoteButtons } from '../components/sections/alternatives-voting/VoteButtons'
import { ClickableUser } from '../components/ClickableUser'

interface SupabaseProfile {
  id: string
  name: string
  avatar_url?: string | null
}

interface SupabaseWord {
  id: string
  word: string
  word_arabic: string
  category_id: string
  category?: {
    id: string
    name: string
    name_english: string
  }
  submitted_by: string
  submitted_at: string
  context: string
  total_votes: number
  alternatives_count: number
  alternatives: SupabaseAlternative[]
  submitter?: SupabaseProfile
}

interface SupabaseAlternative {
  id: string
  egyptian_word?: string
  text?: string
  explanation: string
  net_score?: number
  upvotes?: number
  proposed_by?: string
  proposedBy?: string
  proposed_at?: string
  proposedAt?: string
  proposer?: SupabaseProfile
}

export function Word() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [foreignWord, setForeignWord] = useState<SupabaseWord | null>(null)
  const [userVotes, setUserVotes] = useState<Record<string, VoteType>>({})
  const [users, setUsers] = useState<SupabaseProfile[]>([])

  // Fetch word, alternatives, and user votes from Supabase
  useEffect(() => {
    if (!id) return

    async function fetchData() {
      setLoading(true)
      try {
        // Fetch the foreign word with all related data
        const { data: wordData, error: wordError } = await supabase
          .from('foreign_words')
          .select(`
            *,
            category:categories(*),
            submitter:profiles!foreign_words_submitted_by_fkey(*),
            alternatives(
              *,
              proposer:profiles!alternatives_proposed_by_fkey(*)
            )
          `)
          .eq('id', id)
          .single()

        if (wordError) throw wordError
        if (!wordData) throw new Error('Word not found')

        setForeignWord(wordData)

        // Fetch user's votes if logged in
        if (user && wordData.alternatives) {
          const alternativeIds = wordData.alternatives.map((alt: SupabaseAlternative) => alt.id)

          if (alternativeIds.length > 0) {
            const { data: votesData } = await supabase
              .from('votes')
              .select('alternative_id, vote_type')
              .eq('user_id', user.id)
              .in('alternative_id', alternativeIds)

            const votesMap = votesData?.reduce((acc, vote) => {
              acc[vote.alternative_id] = vote.vote_type
              return acc
            }, {} as Record<string, VoteType>) || {}

            setUserVotes(votesMap)
          }
        }

        // Extract unique users for profiles
        const allUsers = [
          wordData.submitter,
          ...(wordData.alternatives || []).map((alt: SupabaseAlternative) => alt.proposer)
        ].filter(Boolean)

        const uniqueUsers = Array.from(
          new Map(allUsers.map(u => [u.id, u])).values()
        )

        setUsers(uniqueUsers)
      } catch (error) {
        console.error('Error fetching word:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, user])

  const handleVote = async (alternativeId: string, voteType: VoteType) => {
    if (!user) {
      toast.error('يرجى تسجيل الدخول للتصويت')
      return
    }

    try {
      // Upsert vote in database
      const { error } = await supabase
        .from('votes')
        .upsert({
          alternative_id: alternativeId,
          user_id: user.id,
          vote_type: voteType,
        }, {
          onConflict: 'alternative_id,user_id'
        })

      if (error) throw error

      // Update local state optimistically
      setUserVotes(prev => ({
        ...prev,
        [alternativeId]: voteType,
      }))

      // Refetch the word to get updated vote counts
      const { data: wordData } = await supabase
        .from('foreign_words')
        .select(`
          *,
          category:categories(*),
          submitter:profiles!foreign_words_submitted_by_fkey(*),
          alternatives(
            *,
            proposer:profiles!alternatives_proposed_by_fkey(*)
          )
        `)
        .eq('id', id)
        .single()

      if (wordData) {
        setForeignWord(wordData)
      }
    } catch (error) {
      console.error('Error voting:', error)
      toast.error('حدث خطأ أثناء التصويت')
    }
  }

  const handleProposeAlternative = () => {
    navigate(`/word/${id}/alternatives`)
    toast('سيتم توجيهك لصفحة اقتراح البدائل')
  }

  const handleShare = () => {
    if (!foreignWord) return

    const url = window.location.href

    if (navigator.share) {
      navigator
        .share({
          title: `${foreignWord.word_arabic}`,
          text: '',
          url: url,
        })
        .catch((err) => console.log('Error sharing:', err))
    } else {
      navigator.clipboard.writeText(url)
      toast.success('تم نسخ الرابط!')
    }
  }

  const getUserById = (userId: string) => {
    return users.find((u) => u.id === userId)
  }

  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    avatarUrl: user.user_metadata?.avatar_url || null,
  } : undefined

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  if (!foreignWord) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">الكلمة غير موجودة</div>
      </div>
    )
  }

  const submitter = getUserById(foreignWord.submitted_by)

  const transformedAlternatives = (foreignWord.alternatives || []).map((alt) => ({
    id: alt.id,
    text: alt.egyptian_word || alt.text || '',
    explanation: alt.explanation,
    voteCount: alt.net_score ?? Math.max(alt.upvotes ?? 0, 0),
    proposedBy: alt.proposed_by || alt.proposedBy || '',
    proposedAt: alt.proposed_at || alt.proposedAt || '',
  }))

  const topAlternative = transformedAlternatives.length > 0
    ? transformedAlternatives.reduce((max, current) =>
        current.voteCount > max.voteCount ? current : max
      )
    : null

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="sticky top-16 z-30 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-sm text-slate-500 mb-1">{foreignWord.word}</div>
              <h1 className="text-4xl font-bold text-amber-400 tracking-wide">
                {foreignWord.word_arabic}
              </h1>
              {submitter && (
                <div className="flex items-center gap-2 mt-3 text-sm text-slate-400">
                  <span>
                    قدمها <ClickableUser userId={submitter.id} userName={submitter.name} className="text-slate-400" />
                  </span>
                  <span className="text-slate-600">•</span>
                  <span>{new Date(foreignWord.submitted_at).toLocaleDateString('ar-EG', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {currentUser?.id === foreignWord.submitted_by && (
                <button
                  onClick={() => navigate(`/word/${foreignWord.id}/edit`)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  title="تعديل"
                >
                  <svg className="w-5 h-5 text-slate-400 hover:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
              <button
                onClick={handleShare}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                title="شارك"
              >
                <svg className="w-5 h-5 text-slate-400 hover:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6">
        {/* Context */}
        <div>
          <h2 className="text-sm font-semibold text-slate-400 mb-2">
            السياق والاستخدام
          </h2>
          <p className="text-slate-300 leading-relaxed">{foreignWord.context}</p>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

        {/* Alternatives */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-100">
              البدائل المقترحة ({foreignWord.alternatives_count || 0})
            </h2>
            <button
              onClick={handleProposeAlternative}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>اقترح بديل</span>
            </button>
          </div>

          {transformedAlternatives.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="text-slate-400 mb-3">لا توجد بدائل مقترحة حالياً</p>
              <p className="text-sm text-slate-500">كن أول من يقترح بديل لهذه الكلمة</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transformedAlternatives.map((alternative, index) => (
                <div
                  key={alternative.id}
                  className={`relative bg-slate-950 border rounded-xl p-5 transition-all ${
                    topAlternative?.id === alternative.id
                      ? 'border-amber-500/30 bg-amber-500/5'
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="absolute top-3 left-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0
                          ? 'bg-amber-500 text-slate-900'
                          : index === 1
                          ? 'bg-rose-500/20 text-rose-400'
                          : 'bg-slate-800 text-slate-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                  </div>
                  <div className="text-right pr-10 mb-3">
                    <div className="text-2xl font-bold text-slate-100 mb-2">
                      {alternative.text}
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      {alternative.explanation}
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                    <div className="flex items-center gap-3">
                      <VoteButtons
                        alternativeId={alternative.id}
                        upvotes={alternative.voteCount > 0 ? alternative.voteCount : 0}
                        downvotes={alternative.voteCount < 0 ? Math.abs(alternative.voteCount) : 0}
                        userVote={userVotes[alternative.id]}
                        onVote={handleVote}
                      />
                    </div>
                    <div className="text-sm text-slate-500">
                      {getUserById(alternative.proposedBy)?.name && (
                        <span>
                          اقترحها <ClickableUser userId={alternative.proposedBy} userName={getUserById(alternative.proposedBy)!.name} className="text-slate-500" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}